import { useTranslation } from 'react-i18next';
import { ICellRendererParams } from 'ag-grid-community';
import clsx from 'clsx';

import { isObject } from '../../../utils/guard';
import { isSupportedFileIcon, SVGIcon } from '../../icons/SVGIcon';
import { TextWithTooltip } from '../../tooltip/TextWithTooltip';
import { EMPTY_VALUE } from '../valueFormatters';

import { DATA_ATTRIBUTE_PREVENT_AG_GRID_ON_ROW_CLICKED } from './constants';

import styles from './fileNameCellRenderer.module.scss';

export interface IFileNamRendererParams
  extends Partial<ICellRendererParams<Record<string, unknown>>> {
  onFileDownload?: (fileId: string, shouldRefetchTableData?: boolean) => void;
  showFullTextSearchResults?: boolean;
  showUnreadIndicator?: boolean;
}

export const FileNameCellRenderer = ({
  colDef,
  data,
  formatValue,
  onFileDownload,
  showFullTextSearchResults,
  showUnreadIndicator,
  value,
}: IFileNamRendererParams) => {
  const { t } = useTranslation();

  if (value === undefined) {
    return;
  }

  if (value == null) {
    return formatValue?.(value) ?? EMPTY_VALUE;
  }

  const virusInfected = data?.virus_free === false;
  const fileId = data?.id;
  const unread =
    Boolean(showUnreadIndicator) && data?.readByCurrentUser === false;
  const allowFileDownload =
    onFileDownload && !virusInfected && typeof fileId === 'string';

  const getFileName = () => {
    // check if we need to highlight search results
    if (isObject(data?.highlight) && colDef?.field != null) {
      const highlights = data?.highlight?.[colDef.field];
      // we expect an array of strings with each entry indicating exactly one match, i.e.
      // ['<match>Test</match> DocumentTest.pdf', 'Test Document<match>Test</match>.pdf', ...]
      if (Array.isArray(highlights)) {
        // we only consider the item with the first match
        const firstMatch = highlights.find(
          (m): m is string => typeof m === 'string'
        );
        if (firstMatch) {
          return (
            <span
              className={styles['fileName--highlighted']}
              dangerouslySetInnerHTML={{ __html: firstMatch }}
            />
          );
        }
      }
    }
    // use non-highlighted file name
    return value;
  };

  const getFullTextSearchResults = () => {
    if (isObject(data?.highlight) && Array.isArray(data?.highlight?.text)) {
      // we only consider the item with the first match
      const validMatches = data.highlight.text.filter(
        (m): m is string => typeof m === 'string'
      );
      if (validMatches.length > 0) {
        return (
          <>
            {validMatches.length > 1 && (
              <span className={styles['searchResultCount']}>
                {t('app.table.searchResultPagination', {
                  shownResults: 1,
                  totalResults: validMatches.length,
                })}
              </span>
            )}
            <span
              className={styles['searchResultText']}
              dangerouslySetInnerHTML={{
                __html: `(...) ${validMatches[0]} (...)`,
              }}
            />
          </>
        );
      }
    }

    return;
  };

  const fullTextSearchResults = showFullTextSearchResults
    ? getFullTextSearchResults()
    : undefined;

  return (
    <div className={styles['root']}>
      <div
        data-testid="fileNameCellRenderer__fileNameWrapper"
        className={styles['fileNameWrapper']}
      >
        <span
          {...DATA_ATTRIBUTE_PREVENT_AG_GRID_ON_ROW_CLICKED}
          className={styles['fileIcon']}
          onClick={
            allowFileDownload ? () => onFileDownload(fileId, unread) : undefined
          }
        >
          {virusInfected ? (
            <TextWithTooltip
              tooltipProps={{
                disabled: false,
                label: t('app.table.tooltip.maliciousContent'),
              }}
            >
              <SVGIcon name="file-alert" />
            </TextWithTooltip>
          ) : (
            <SVGIcon name={mapFileNameToSVGFileIcon(value)} />
          )}
        </span>
        <span
          {...DATA_ATTRIBUTE_PREVENT_AG_GRID_ON_ROW_CLICKED}
          data-testid={`fileNameCellRenderer__fileName--${unread ? 'unread' : 'read'}`}
          className={clsx(styles['fileName'], {
            [styles['fileName--malicious']]: virusInfected,
            [styles['fileName--unread']]: unread,
          })}
          onClick={
            allowFileDownload ? () => onFileDownload(fileId, unread) : undefined
          }
        >
          {getFileName()}
        </span>
      </div>
      {fullTextSearchResults && (
        <div
          data-testid="fileNameCellRenderer__searchResultWrapper"
          className={styles['searchResultWrapper']}
        >
          {fullTextSearchResults}
        </div>
      )}
    </div>
  );
};

function mapFileNameToSVGFileIcon(fileName: string) {
  const fileIconName = `file-${fileName.split('.').pop()?.toLowerCase()}`;
  return isSupportedFileIcon(fileIconName) ? fileIconName : 'file-unknown';
}
